import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "OcrVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    ocr_verification_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        document_details: false,
        authenticity_analysis: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    safe_ocr_data() {
      const data = this.ocr_verification_data || {};
      
      // If data has a 'result' property, use that as the main data
      // This handles the case where the API response structure includes status info
      if (data.result && typeof data.result === 'object') {
        return data.result;
      }
      
      return data;
    },

    // Document Details
    document_details() {
      return this.safe_ocr_data.document_details || {};
    },

    document_images() {
      return this.safe_ocr_data.document_images || {};
    },

    document_authenticity() {
      return this.safe_ocr_data.document_authenticity || {};
    },

    document_type() {
      return this.safe_ocr_data.document_type_info || {};
    },

    // Personal Information - Enhanced to handle different document types
    full_name() {
      return this.document_details["Surname And Given Names"] || 
             this.document_details["Full Name"] || 
             this.document_details["Name"] || 
             "N/A";
    },

    given_names() {
      return this.document_details["Given Names"] || 
             this.document_details["First Name"] || 
             this.document_details["FirstName"] || 
             "N/A";
    },

    surname() {
      return this.document_details["Surname"] || 
             this.document_details["Last Name"] || 
             this.document_details["LastName"] || 
             "N/A";
    },

    date_of_birth() {
      return this.document_details["Date of Birth"] || 
             this.document_details["DOB"] || 
             this.document_details["Birth Date"] || 
             "N/A";
    },

    formatted_date_of_birth() {
      return this.formatDate(this.date_of_birth);
    },

    age() {
      return this.document_details["Age"] || "N/A";
    },

    sex() {
      return this.document_details["Sex"] || "N/A";
    },

    formatted_sex() {
      return this.sex === "M" ? "Male" : this.sex === "F" ? "Female" : this.sex;
    },

    personal_number() {
      return this.document_details["Personal Number"] || 
             this.document_details["ID Number"] || 
             this.document_details["Document Number"] || 
             "N/A";
    },

    citizenship_status() {
      return this.document_details["Citizenship status"] || 
             this.document_details["Citizenship"] || 
             this.document_details["Status"] || 
             this.document_details["Nationality"] ||
             "N/A";
    },

    nationality() {
      return this.document_details["Nationality"] || 
             this.document_details["Nationality Code"] || 
             "N/A";
    },

    // Display computed properties
    display_full_name() {
      // Try multiple possible field names for full name
      return this.document_details["Surname And Given Names"] || 
             this.document_details["Full Name"] || 
             (this.given_names !== "N/A" && this.surname !== "N/A" ? `${this.surname} ${this.given_names}` : "") ||
             this.document_details["Name"] || 
             "N/A";
    },

    formatted_citizenship_status() {
      const status = this.citizenship_status;
      if (status === "N/A") return "N/A";
      
      // Handle different citizenship status formats
      if (status.toLowerCase().includes("citizen")) return "Citizen";
      if (status.toLowerCase().includes("permanent resident")) return "Permanent Resident";
      if (status.toLowerCase().includes("non-citizen")) return "Non-Citizen";
      
      return status;
    },

    formatted_nationality() {
      const nat = this.nationality;
      if (nat === "N/A") return "N/A";
      
      // Handle different nationality formats
      if (nat === "RSA" || nat === "ZAF") return "South African";
      if (nat.toLowerCase().includes("south africa")) return "South African";
      
      return nat;
    },

    formatted_citizenship_phrase() {
      const citizenship = this.formatted_citizenship_status;
      if (citizenship === "N/A") return "";
      
      if (citizenship === "Citizen") return "citizen";
      if (citizenship === "Permanent Resident") return "permanent resident";
      if (citizenship === "Non-Citizen") return "non-citizen";
      
      return citizenship.toLowerCase();
    },

    // Document type detection
    is_driving_license() {
      return this.document_description.toLowerCase().includes("driving license") || 
             this.document_description.toLowerCase().includes("driver's license");
    },

    show_nationality_details() {
      return !this.is_driving_license;
    },

    place_of_birth() {
      return this.document_details["eID PlaceOfBirth Country"] || 
             this.document_details["Place of Birth"] || 
             this.document_details["Birth Country"] || 
             "N/A";
    },

    issuing_state() {
      return this.document_details["Issuing State Name"] || 
             this.document_details["Issuing Country"] || 
             this.document_details["Authority"] || 
             "N/A";
    },

    issuing_country() {
      return this.document_type.dCountryName || 
             this.document_details["Issuing State Name"] || 
             this.document_details["Issuing Country"] || 
             "N/A";
    },

    // Document Type Information
    document_description() {
      return this.document_type.dDescription || "N/A";
    },

    document_year() {
      if (this.document_type.dYear) {
        return this.document_type.dYear;
      }

      const issueYear = this.extractYearFromDate(this.date_of_issue_value);
      return issueYear || "N/A";
    },

    icao_code() {
      return this.document_type.ICAOCode || "N/A";
    },

    pages_count() {
      return this.document_authenticity.pagesCount || "N/A";
    },

    mrz_present() {
      const dMRZ = this.document_type.dMRZ;
      if (dMRZ === true) return "Yes";
      if (dMRZ === false) return "No";
      return "Unknown";
    },

    document_format() {
      const format_map = {
        0: "Physical Document",
        1: "Digital Document", 
        2: "Standard Document",
        1002: "Physical Card Format"
      };
      return format_map[this.document_type.dFormat] || `Format ${this.document_type.dFormat}`;
    },

    document_status_text() {
      const isDeprecated = this.document_type.isDeprecated;
      if (isDeprecated === true) return "Deprecated";
      if (isDeprecated === false) return "Active";
      return "Unknown";
    },

    date_of_issue_value() {
      return (
        this.document_details["Date of Issue"] ||
        this.document_details["Issue Date"] ||
        this.document_details["DateOfIssue"] ||
        this.document_details["IssueDate"] ||
        null
      );
    },

    formatted_date_of_issue() {
      const raw = this.date_of_issue_value;
      if (!raw) return "N/A";

      const formatted = this.formatDate(raw);
      return formatted === "N/A" && raw ? raw : formatted;
    },

    display_date_of_issue() {
      return this.formatted_date_of_issue !== "N/A"
        ? this.formatted_date_of_issue
        : this.date_of_issue_value || "N/A";
    },

    date_of_expiry_value() {
      return (
        this.document_details["Date of Expiry"] ||
        this.document_details["Expiry Date"] ||
        this.document_details["DateOfExpiry"] ||
        this.document_details["ExpiryDate"] ||
        null
      );
    },

    formatted_date_of_expiry() {
      const raw = this.date_of_expiry_value;
      if (!raw) return "N/A";

      const formatted = this.formatDate(raw);
      return formatted === "N/A" && raw ? raw : formatted;
    },

    display_date_of_expiry() {
      return this.formatted_date_of_expiry !== "N/A"
        ? this.formatted_date_of_expiry
        : this.date_of_expiry_value || "N/A";
    },

    // Document Images
    portrait_image() {
      const image = this.document_images["Portrait"] || null;
      if (!image) return null;

      // Check if it's already a URL (starts with http/https) or data URL
      if (image.startsWith("http") || image.startsWith("data:")) return image;

      // Otherwise, assume it's a raw base64 string and add the prefix
      return `data:image/jpeg;base64,${image}`;
    },

    signature_image() {
      const image = this.document_images["Signature"] || null;
      if (!image) return null;

      // Check if it's already a URL (starts with http/https) or data URL
      if (image.startsWith("http") || image.startsWith("data:")) return image;

      // Otherwise, assume it's a raw base64 string and add the prefix
      return `data:image/jpeg;base64,${image}`;
    },

    document_front_side() {
      return this.document_images["Document front side"] || null;
    },

    // Authenticity Analysis
    overall_status() {
      return this.document_authenticity.overallStatus || 0;
    },

    overall_status_text() {
      const status_map = {
        0: "Not Checked",
        1: "Passed",
        2: "Failed",
        3: "Not Available",
      };
      return status_map[this.overall_status] || "Unknown";
    },

    overall_status_class() {
      const class_map = {
        0: "text-info",
        1: "text-success",
        2: "text-danger",
      };
      return class_map[this.overall_status] || "text-muted";
    },

    overall_score_percentage() {
      // Convert status to percentage (0=100%, 1=75%, 2=25%)
      const score_map = {
        0: 100,
        1: 75,
        2: 25,
      };
      return score_map[this.overall_status] || 0;
    },
    authenticity_checks() {
      return [
        {
          name: "docType",
          label: "Document Type",
          value: this.document_authenticity.docType || 0,
          ...this.get_check_details(this.document_authenticity.docType || 0),
        },
        {
          name: "security",
          label: "Security Features",
          value: this.document_authenticity.security || 0,
          ...this.get_check_details(this.document_authenticity.security || 0),
        },
        {
          name: "text",
          label: "Text Analysis",
          value: this.document_authenticity.text || 0,
          ...this.get_check_details(this.document_authenticity.text || 0),
        },
        {
          name: "imageQA",
          label: "Image Quality",
          value: this.document_authenticity.imageQA || 0,
          ...this.get_check_details(this.document_authenticity.imageQA || 0),
        },
        {
          name: "expiry",
          label: "Expiry Check",
          value: this.document_authenticity.expiry || 0,
          ...this.get_check_details(this.document_authenticity.expiry || 0),
        },
        {
          name: "mrz",
          label: "MRZ Validation",
          value: this.document_authenticity.mrz || 0,
          ...this.get_check_details(this.document_authenticity.mrz || 0),
        },
        {
          name: "vds",
          label: "VDS Security",
          value: this.document_authenticity.vds || 0,
          ...this.get_check_details(this.document_authenticity.vds || 0),
        },
      ];
    },

    // Individual status helpers for technical details
    security_status_text() {
      return this.get_status_text(this.document_authenticity.security || 0);
    },

    security_badge_class() {
      return this.get_badge_class(this.document_authenticity.security || 0);
    },

    text_status_text() {
      return this.get_status_text(this.document_authenticity.text || 0);
    },

    text_badge_class() {
      return this.get_badge_class(this.document_authenticity.text || 0);
    },

    image_qa_status_text() {
      return this.get_status_text(this.document_authenticity.imageQA || 0);
    },

    image_qa_badge_class() {
      return this.get_badge_class(this.document_authenticity.imageQA || 0);
    },

    doc_type_status_text() {
      return this.get_status_text(this.document_authenticity.docType || 0);
    },

    doc_type_badge_class() {
      return this.get_badge_class(this.document_authenticity.docType || 0);
    },

    expiry_status_text() {
      return this.get_status_text(this.document_authenticity.expiry || 0);
    },

    expiry_badge_class() {
      return this.get_badge_class(this.document_authenticity.expiry || 0);
    },

    mrz_status_text() {
      return this.get_status_text(this.document_authenticity.mrz || 0);
    },

    mrz_badge_class() {
      return this.get_badge_class(this.document_authenticity.mrz || 0);
    },

    vds_status_text() {
      return this.get_status_text(this.document_authenticity.vds || 0);
    },

    vds_badge_class() {
      return this.get_badge_class(this.document_authenticity.vds || 0);
    }, // Report metadata
    report_reference() {
      return this.generate_report_reference();
    },

    verification_date() {
      return this.format_current_date();
    },

    generated_date() {
      return this.format_current_date();
    },

    // Security Analysis Summary
    passed_checks_count() {
      return this.authenticity_checks.filter((check) => check.value === 1)
        .length;
    },

    info_checks_count() {
      return this.authenticity_checks.filter((check) => check.value === 0)
        .length;
    },
    failed_checks_count() {
      return this.authenticity_checks.filter((check) => check.value === 2)
        .length;
    },

    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },


    document_image_items() {
      const images = [];
      const documentImages = this.document_images || {};
      
      const imageConfig = {
        'Document front side': { 
          label: 'Document Front Side', 
          layout: 'full', 
          description: 'Full document scan' 
        },
        'Portrait': { 
          label: 'Portrait', 
          layout: 'half', 
          description: 'Document holder photo' 
        },
        'Signature': { 
          label: 'Signature', 
          layout: 'half', 
          description: 'Document holder signature' 
        },
        'Fingerprint': { 
          label: 'Fingerprint', 
          layout: 'half', 
          description: 'Document holder fingerprint' 
        },
        'Barcode': { 
          label: 'Barcode', 
          layout: 'half', 
          description: 'Document barcode' 
        }
      };

      const imageOrder = [
        'Document front side', // Full layout images first
        'Portrait', 'Signature', 'Fingerprint', 'Barcode' // Half layout images
      ];
      
      imageOrder.forEach(imageKey => {
        if (documentImages[imageKey] && imageConfig[imageKey]) {
          const config = imageConfig[imageKey];
          const imageData = documentImages[imageKey];
          
          // Process the image source - handle both URLs and base64 data
          let processedSrc = imageData;
          if (imageData && typeof imageData === 'string') {
            // If it's base64 data without data URL prefix, add it
            if (!imageData.startsWith('http') && !imageData.startsWith('data:')) {
              processedSrc = `data:image/jpeg;base64,${imageData}`;
            }
          }
          
          images.push({
            key: imageKey.toLowerCase().replace(/\s+/g, '_'),
            layout: config.layout,
            label: config.label,
            src: processedSrc,
            hasImage: !!imageData,
            description: config.description
          });
        }
      });
      

      const excludedImages = ['Ghost portrait', 'Ghost Portrait'];
      
      Object.keys(documentImages).forEach(imageKey => {
        if (!imageConfig[imageKey] && documentImages[imageKey] && !excludedImages.includes(imageKey)) {
          const imageData = documentImages[imageKey];
     
          let processedSrc = imageData;
          if (imageData && typeof imageData === 'string') {
            
            if (!imageData.startsWith('http') && !imageData.startsWith('data:')) {
              processedSrc = `data:image/jpeg;base64,${imageData}`;
            }
          }
          
          images.push({
            key: imageKey.toLowerCase().replace(/\s+/g, '_'),
            layout: 'half', // Default to half layout for unknown images
            label: imageKey, // Use the original key as label
            src: processedSrc,
            hasImage: !!imageData,
            description: `Document ${imageKey.toLowerCase()}`
          });
        }
      });
      
      return images;
    },

    document_specific_sections() {
      const sections = [];
      const details = this.document_details || {};

      if (!Object.keys(details).length) {
        return sections;
      }

      const addField = (fields, label, value) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'N/A') {
          fields.push({ label, value });
        }
      };

      if (this.is_driving_license) {
        const licenceFields = [];
        addField(licenceFields, "Document Number", details["Document Number"] || details["Licence Number"]);
        addField(licenceFields, "License Class", details["DL Class"] || details["Licence Class"] || details["Category"]);
        addField(licenceFields, "Date of Issue", this.display_date_of_issue);
        addField(licenceFields, "Date of Expiry", this.display_date_of_expiry);
        addField(licenceFields, "Remainder Term", details["RemainderTerm"]);
        addField(licenceFields, "Years Since Issue", details["Years Since Issue"]);
        addField(licenceFields, "Age at Issue", details["Age at Issue"]);
        addField(licenceFields, "Authority", details["Authority"]);

        if (licenceFields.length) {
          sections.push({
            title: "Driving Licence Details",
            fields: licenceFields,
          });
        }
      }

      return sections;
    },
  },

  methods: {
    formatDate(date) {
      return format_date(date);
    },

    extractYearFromDate(dateValue) {
      if (!dateValue) return null;

      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return String(date.getFullYear());
      }

      // Handle formats that may not be parsed by Date (e.g., DD/MM/YYYY)
      const match = /^(\d{4})[-/]/.exec(dateValue);
      if (match) {
        return match[1];
      }

      const tailMatch = /[-/]?(\d{4})$/.exec(dateValue);
      if (tailMatch) {
        return tailMatch[1];
      }

      return null;
    },

    format_current_date() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    generate_report_reference() {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000);
      return `OCR-${timestamp}-${random}`;
    },

    get_status_text(value) {
      const status_map = {
        0: "Not Checked",
        1: "Passed",
        2: "Failed",
        3: "Not Available", 
      };
      return status_map[value] || "Unknown";
    },

    get_badge_class(value) {
      const class_map = {
        0: "badge-info",
        1: "badge-success",
        2: "badge-danger",
        3: "badge-secondary",
      };
      return class_map[value] || "badge-secondary";
    },
    get_check_details(value) {
      const details_map = {
        0: {
          status_text: "Not Checked",
          badge_class: "badge-info",
          progress_class: "bg-info",
          percentage: 100,
        },
        1: {
          status_text: "Passed",
          badge_class: "badge-success",
          progress_class: "bg-success",
          percentage: 100,
        },
        2: {
          status_text: "Failed",
          badge_class: "badge-danger",
          progress_class: "bg-danger",
          percentage: 0,
        },
        3: {
          status_text: "Not Available",
          badge_class: "badge-secondary",
          progress_class: "bg-secondary",
          percentage: 0,  
        }
      };
      return (
        details_map[value] || {
          status_text: "Unknown",
          badge_class: "badge-secondary",
          progress_class: "bg-secondary",
          percentage: 0,
        }
      );
    },
    get_status_color_class(value) {
      const color_map = {
        0: "text-info",
        1: "text-success",
        2: "text-danger",
        3: "text-muted",
      };
      return color_map[value] || "text-muted";
    },

    close_modal() {
      $("#export-modal-" + this.report_reference).modal("hide");
    },

    show_export_modal() {
      this.export_options.document_details = true;
      this.export_options.authenticity_analysis = true;
      this.export_options.raw_json = false;

      $("#export-modal-" + this.report_reference).modal("show");
    },

    async load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async ensure_libraries_loaded() {
      try {
        if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
          if (typeof Papa === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
            );
          }
          this.libraries_loaded.papa_parse = true;
        }

        if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
          if (typeof XLSX === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
            );
          }
          this.libraries_loaded.sheetjs = true;
        }
      } catch (error) {
        throw new Error("Failed to load export libraries");
      }
    },

    get_current_date_string() {
      const current_date = new Date();
      return current_date.toISOString().split("T")[0];
    },

    prepare_document_details_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Full Name": this.full_name,
          "Given Names": this.given_names,
          Surname: this.surname,
          "Date of Birth": this.formatted_date_of_birth,
          Age: this.age,
          Sex: this.formatted_sex,
          "Personal Number": this.personal_number,
          "Citizenship Status": this.citizenship_status,
          Nationality: this.nationality,
          "Place of Birth": this.place_of_birth,
          "Document Type": this.document_description,
          "Issuing Country": this.issuing_country,
          "Document Year": this.document_year,
          "ICAO Code": this.icao_code,
          "Document Format": this.document_format,
          "Date of Issue": this.display_date_of_issue,
          "Date of Expiry": this.display_date_of_expiry,
          "Pages Count": this.pages_count,
          "MRZ Present": this.mrz_present,
          "Overall Status": this.overall_status_text,
          "Generated Date": this.generated_date,
        },
      ];
    },

    prepare_authenticity_analysis_data() {
      return this.authenticity_checks.map((check) => ({
        "Check Name": check.label,
        Status:
          check.value === 0
            ? "Not Checked"
            : check.value === 1
            ? "Passed"
            : "Failed",
        "Status Code": check.value,
        Description: this.get_check_description(check.name),
      }));
    },

    get_check_description(check_name) {
      const descriptions = {
        docType:
          "Validates the document type against known templates and formats",
        security:
          "Examines security features like watermarks, holograms, and special inks",
        text: "Analyzes text quality, fonts, and formatting for consistency",
        imageQA: "Evaluates image quality, resolution, and potential tampering",
        expiry: "Checks if the document has expired or is still valid",
        mrz: "Validates Machine Readable Zone data and checksums",
        vds: "Verifies Visible Digital Signature security features",
      };
      return (
        descriptions[check_name] || "Authentication check performed on document"
      );
    },

    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
        },
        ocr_verification_data: this.safe_ocr_data,
      };
    },

    async export_csv() {
      const Papa = window.Papa;
      const current_date = this.get_current_date_string();
      const tables_to_export = [];

      if (this.export_options.document_details) {
        const document_data = this.prepare_document_details_data();
        const csv_content = Papa.unparse(document_data);
        tables_to_export.push({
          name: `OCR_Report_DocumentDetails_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.authenticity_analysis) {
        const auth_data = this.prepare_authenticity_analysis_data();
        const csv_content = Papa.unparse(auth_data);
        tables_to_export.push({
          name: `OCR_Report_AuthenticityAnalysis_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        const json_content = JSON.stringify(json_data, null, 2);
        tables_to_export.push({
          name: `OCR_Report_RawData_${current_date}.json`,
          content: json_content,
          type: "application/json",
        });
      }

      tables_to_export.forEach((table) => {
        const blob = new Blob([table.content], {
          type: table.type || "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", table.name);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },

    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = this.get_current_date_string();
      const workbook = XLSX.utils.book_new();

      if (this.export_options.document_details) {
        const document_data = this.prepare_document_details_data();
        const worksheet = XLSX.utils.json_to_sheet(document_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Document Details");
      }

      if (this.export_options.authenticity_analysis) {
        const auth_data = this.prepare_authenticity_analysis_data();
        const worksheet = XLSX.utils.json_to_sheet(auth_data);
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Authenticity Analysis"
        );
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, we'll create a simple table with the JSON as text
        const json_table = [
          {
            "JSON Data": JSON.stringify(json_data, null, 2),
          },
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raw JSON Data");
      }

      const filename = `OCR_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },

    async export_json() {
      const current_date = this.get_current_date_string();
      const export_data = {};

      if (this.export_options.document_details) {
        export_data.document_details = this.prepare_document_details_data()[0];
      }

      if (this.export_options.authenticity_analysis) {
        export_data.authenticity_analysis =
          this.prepare_authenticity_analysis_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      const json_content = JSON.stringify(export_data, null, 2);
      const blob = new Blob([json_content], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `OCR_Report_${current_date}.json`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async export_data() {
      try {
        await this.ensure_libraries_loaded();
        if (this.export_format === "csv") {
          await this.export_csv();
        } else if (this.export_format === "excel") {
          await this.export_excel();
        } else if (this.export_format === "json") {
          await this.export_json();
        }
        $("#export-modal-" + this.report_reference).modal("hide");
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },
  },
};